import useFirestore from "./useFirestore";
import { ContactColumnsId, initialContactColumns } from "utils/contacts/table";
import type { ActiveColumns, ActiveFilters, ContactColumns } from "utils/contacts/table";

type AvailableColumnsOptions = {
  /**
   * Custom active columns, used when to check whether column is active or not
   */
  activeColumns?: ActiveColumns;
  /**
   * Filters out columns that are not active based on the filter object
   */
  activeFilters?: ActiveFilters;
  /**
   * Array of custom columns, used when want to override default columns
   */
  contactColumns?: ContactColumns[];
  /**
   * Check if user is still loading for showing real estate columns
   */
  checkLoadingUser?: boolean;
  /**
   * Include custom filters in the available columns
   */
  includeCustomFilters?: boolean;
};

export function useAvailableColumns({
  activeColumns,
  activeFilters,
  contactColumns,
  checkLoadingUser,
  includeCustomFilters
}: AvailableColumnsOptions = {}): ContactColumns[] {
  const { userSettings, isLoadingUser } = useFirestore();

  const isRealEstateUser = userSettings?.crmType === "realEstate";

  let rawColumns = contactColumns ?? initialContactColumns;

  if (includeCustomFilters) {
    rawColumns = [...customFilters, ...rawColumns];

    rawColumns = rawColumns.filter(({ id }) => !removedFiltersOnCustomFilters.includes(id));
  }

  const filteredColumns = rawColumns.filter(({ id, onlyRealEstate }) => {
    const isCustomFilter = customFilters.some((column) => column.id === id);
    const isColumnExists = initialContactColumns.some((column) => column.id === id);

    const showColumn = isCustomFilter || isColumnExists;

    if (!showColumn) return false;

    const isActiveColumn = activeColumns ? activeColumns?.[id] : true;
    const isActiveFilter = activeFilters ? activeFilters?.[id]?.active : true;

    const isStillLoadingUser = checkLoadingUser ? !isLoadingUser : true;
    const isVisibleToUser = isStillLoadingUser && onlyRealEstate ? isRealEstateUser : true;

    return isActiveColumn && isActiveFilter && isVisibleToUser;
  });

  return filteredColumns;
}

const customFilters: ContactColumns[] = [
  {
    id: "sortBy"
  },
  {
    id: "rooms"
  },
  {
    id: "hasMeetingOrTask"
  }
];

const removedFiltersOnCustomFilters: ContactColumnsId[] = ["roomsMin", "roomsMax"];
